/**
 * Taking the phone out.
 *
 * The mask is the phone and nothing else. Measured on real selfies, adding the
 * fingers wrapped around it always looked worse: the inpainter leaves a stump
 * where a hand was, whereas a hand with the phone gone reads as a hand at rest.
 *
 * A phone over the face is not erased at all. The inpainter would paint in a
 * face, and it would not be this person's; there is no honest way to do it from
 * one photograph, so the caller is told and the frame is left alone.
 */
import { fillHole, type EraserInfo } from '../ml/inpaint';
import { findPhones, type Scene } from './find';
import { FACE_LIMIT, type Box, type Phone } from './geometry';
import { growSlab, slabWindow } from './slab';

export interface EraseResult {
  canvas: HTMLCanvasElement;
  scene: Scene;
  /** Phones taken out. */
  erased: Phone[];
  /** Phones left in because they hide the face. */
  refused: Phone[];
  /** The hole that was filled, one byte per pixel. */
  hole: Uint8Array | null;
  eraser: EraserInfo | null;
  ms: { find: number; fill: number };
}

/** How far past the detector's box the hole extends, as a share of the phone's short side. */
const MARGIN = 0.12;

/** Rasterise phones into a hole mask, each grown by a margin for its case edge and shadow. */
export function holeFor(phones: Box[], w: number, h: number): Uint8Array {
  const hole = new Uint8Array(w * h);
  for (const b of phones) {
    const m = Math.max(3, Math.round(Math.min(b.w, b.h) * MARGIN));
    const x0 = Math.max(0, Math.floor(b.x - m));
    const y0 = Math.max(0, Math.floor(b.y - m));
    const x1 = Math.min(w, Math.ceil(b.x + b.w + m));
    const y1 = Math.min(h, Math.ceil(b.y + b.h + m));
    for (let y = y0; y < y1; y++) hole.fill(1, y * w + x0, y * w + x1);
  }
  return hole;
}

/** Grow a mask outward by `r` pixels, in place over a full-frame hole. */
function dilateInto(hole: Uint8Array, w: number, h: number, r: number): Uint8Array {
  if (r <= 0) return hole;
  // Two passes of a 1-D running maximum: a square structuring element, which
  // at a few pixels is indistinguishable from a disc and much cheaper.
  const tmp = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    let last = -Infinity;
    for (let x = 0; x < w; x++) {
      if (hole[y * w + x]) last = x;
      if (x - last <= r) tmp[y * w + x] = 1;
    }
    last = Infinity;
    for (let x = w - 1; x >= 0; x--) {
      if (hole[y * w + x]) last = x;
      if (last - x <= r) tmp[y * w + x] = 1;
    }
  }
  const out = new Uint8Array(w * h);
  for (let x = 0; x < w; x++) {
    let last = -Infinity;
    for (let y = 0; y < h; y++) {
      if (tmp[y * w + x]) last = y;
      if (y - last <= r) out[y * w + x] = 1;
    }
    last = Infinity;
    for (let y = h - 1; y >= 0; y--) {
      if (tmp[y * w + x]) last = y;
      if (last - y <= r) out[y * w + x] = 1;
    }
  }
  return out;
}

/**
 * The hole for a set of phones: each one grown from its box to the whole slab
 * (see slab.ts), then widened by a margin for the case edge and the soft
 * shadow it casts.
 */
export function holeForPhones(source: HTMLCanvasElement, phones: Box[]): Uint8Array {
  const { width: w, height: h } = source;
  const ctx = source.getContext('2d', { willReadFrequently: true });
  if (!ctx) return holeFor(phones, w, h);
  let hole: Uint8Array = new Uint8Array(w * h);
  let margin = 3;
  for (const box of phones) {
    const win = slabWindow(box, w, h);
    if (win.w < 2 || win.h < 2) continue;
    const slab = growSlab(ctx.getImageData(win.x, win.y, win.w, win.h).data, win, box);
    for (let y = 0; y < win.h; y++) {
      for (let x = 0; x < win.w; x++) if (slab.mask[y * win.w + x]) hole[(win.y + y) * w + win.x + x] = 1;
    }
    margin = Math.max(margin, Math.round(Math.min(box.w, box.h) * MARGIN));
  }
  hole = dilateInto(hole, w, h, margin);
  return hole;
}

export async function erasePhones(source: HTMLCanvasElement, scene?: Scene): Promise<EraseResult> {
  const found = scene ?? (await findPhones(source));
  const refused = found.phones.filter((p) => p.coversFace > FACE_LIMIT);
  const erased = found.phones.filter((p) => p.coversFace <= FACE_LIMIT);

  if (erased.length === 0) {
    return { canvas: source, scene: found, erased, refused, hole: null, eraser: null, ms: { find: found.ms, fill: 0 } };
  }
  const hole = holeForPhones(source, erased.map((p) => p.box));
  const filled = await fillHole(source, hole);
  return {
    canvas: filled.canvas,
    scene: found,
    erased,
    refused,
    hole,
    eraser: filled.eraser,
    ms: { find: found.ms, fill: filled.ms },
  };
}
